import { DragEvent, useRef, useState } from "react";
import { ErrorToast, SuccessToast } from "../toastMessage";
import { TSchematicPlanFile, useUploadSchematicFile } from "../../hooks/schematic-plan.hooks";
import {
  formatFileSize,
  SCHEMATIC_FILE_ACCEPT,
  schematicFileProblem,
} from "./schematicFileRules";

type Props = {
  applicationId: number;
  /** The file saved for the application, if any. */
  file: TSchematicPlanFile | null;
};

/**
 * Upload box for the Schematic Plan file: drag and drop or browse, a
 * progress bar while it is sent, then a preview of the saved file with
 * Replace. The file is saved on upload; the choice is saved by the step's
 * Next button (SchematicPlanStep).
 */
export function SchematicFileUpload({ applicationId, file }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const upload = useUploadSchematicFile(applicationId);

  const send = (picked: File | undefined) => {
    if (!picked || upload.isPending) return;
    const problem = schematicFileProblem(picked);
    if (problem) {
      ErrorToast(problem);
      return;
    }
    setProgress(0);
    upload.mutate(
      { file: picked, onProgress: setProgress },
      {
        onSuccess: (result) => {
          if (result?.success) SuccessToast("Schematic plan file uploaded");
        },
        onSettled: () => setProgress(null),
      }
    );
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 1) {
      ErrorToast("Upload one file only.");
      return;
    }
    send(event.dataTransfer.files[0]);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept={SCHEMATIC_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          send(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") openPicker();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging ? "border-primary bg-blue-50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <p className="font-semibold text-[#423F3F]">
          {file ? "Drop a new file here to replace it, or click to browse" : "Drop the schematic plan file here, or click to browse"}
        </p>
        <p className="text-sm text-gray-500 mt-1">One PDF, JPG or PNG file, up to 10 MB</p>
      </div>

      {progress !== null && (
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Uploading…</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-2 bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {file && (
        <div className="border rounded-lg p-3 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="min-w-0">
              <p className="font-semibold text-[#423F3F] break-all">{file.name}</p>
              <p className="text-sm text-gray-500">
                {file.fileType === "pdf" ? "PDF" : "Image"}
                {file.size ? ` · ${formatFileSize(file.size)}` : ""}
                {file.updatedByRole ? ` · uploaded by ${file.updatedByRole}` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="border border-primary text-primary px-4 py-1 rounded-full text-sm font-semibold"
              >
                Open
              </a>
              <button
                type="button"
                onClick={openPicker}
                disabled={upload.isPending}
                className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold disabled:bg-gray-300"
              >
                Replace
              </button>
            </div>
          </div>
          {file.fileType === "pdf" ? (
            <iframe
              title="Schematic plan preview"
              src={file.url}
              className="w-full h-[600px] border rounded"
            />
          ) : (
            <img
              src={file.url}
              alt="Schematic plan"
              className="w-full max-h-[600px] object-contain border rounded"
            />
          )}
        </div>
      )}
    </div>
  );
}
